import React, {useContext, useEffect, useState} from "react";
import {Pressable, Image} from "react-native";
import MainStyle from "../../MainStyle";
import TournamentHeader from "./TournamentHeader";
import PrizePool from "./PrizePool";
import {Tournament, TournamentPlayerBet_State, TournamentPrizeDistributionItem} from "../../generated/smg_service_pb";
import {useNavigation} from "@react-navigation/native";
import {colors} from "../assets/styles/colors";
import {CancelModalProps, ExtendedUserDataType} from "../types";
import moment from "moment";
import {tournamentShotsBeforeEntry, tournamentStatusStyle} from "../utils";
import Div from "../lib/Div";
import P from "../lib/P";
import TournamentHelperText from "./TournamentHelperText";
import {isFullSwing} from "../constants";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import ServiceClient from "../utils/ServiceClient";
import {StoreContext} from "./StoreContextProvider";

type Props = {
  item: Tournament, w?: string | number, mx?: string | number, mb?: string | number, getUser: () => void,
  extendedUserData: ExtendedUserDataType, inFocus?: boolean
};

const TournamentItem = (props: Props) => {
  const {item, w, mx, mb, extendedUserData, inFocus} = props;
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const restrictEntry = tournamentShotsBeforeEntry(extendedUserData);
  const registered = !!item.currentPlayerDetails;
  const areShotsFinished = item.currentPlayerDetails?.bets.filter(bet => bet.state === TournamentPlayerBet_State.FINISHED).length === item.totalShots;
  let activeColor = isFullSwing ? '#3369FF' : colors.newGreenText;
  const statusStyle = tournamentStatusStyle(item.state);
  const [prizeDistribution, setPrizeDistribution] = useState<TournamentPrizeDistributionItem[]>();
  const Context = useContext(StoreContext);
  const {cancelModalConfig} = Context;
  let message = '';

  if (moment.unix(Number(item.entryBeginDate)).isAfter()) {
    message = 'Starts In:';

  } else if (!registered) {
    message = 'Ends In:';

  } else if (restrictEntry > 0) {
    message = `Tournament Starts After ${restrictEntry} Shot${restrictEntry !== 1 ? 's' : ''}`;
    activeColor = colors.gray;

  } else if (!areShotsFinished) {
    message = 'Shots Not Completed. Ends In:';

  } else if (areShotsFinished) {
    message = 'Shots Completed. Ends In:';
  }

  const getTournamentPrizeDistribution = () => {
    ServiceClient.getTournamentPrizeDistribution(item.id).then(res => {
      setPrizeDistribution(res?.items);

    }).catch(err => {
      console.log('getTournamentPrizeDistribution err', err);
      Context.setLoadingOverlay(false);

      let conf: CancelModalProps = {
        visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        }
      };

      Context.setCancelModalConfig(conf);
    });
  };

  useEffect(() => {
    if (!!item.seriesId) getTournamentPrizeDistribution();
  }, []);

  return (
    <Div mx={mx} w={w} mb={mb} borderColor={activeColor} rounded={isFullSwing ? 4 : 20} bg={colors.mainBg}
         borderWidth={1} overflow="hidden">
      <TournamentHelperText
        bg={activeColor} restrictEntry={restrictEntry} inFocus={inFocus} entrybegindate={Number(item.entryBeginDate)}
        expirationdate={Number(item.expirationDate)} message={message}
      />

      <P ta="center" fw="500" fs={24} numberOfLines={isFullSwing ? 2 : 1} mb={isFullSwing ? 4 : 0} mx={10}>
        {item.name}
      </P>
      {!isFullSwing && <P color={colors.id_text} ta="center" fw="500" fs={20} mb={5}>#{item.id}</P>}

      {isFullSwing && <Div bg="#676767" h={1} my={9} mx={11}/>}

      {!isFullSwing && (
        <>
          {!!item.sponsorLandscapeLogo ? (
            <Div h={72} w={120} mb={8} bg="white" rounded={6} mx="auto" overflow="hidden">
              <Image
                source={{uri: item.sponsorLandscapeLogo}} resizeMode="contain"
                style={[MainStyle.absolute, MainStyle.h_271, MainStyle.t__99, MainStyle.w_271, MainStyle.l__75]}
              />
            </Div>
          ) : !!item.sponsorLogo ? (
            <Div h={72} w={72} mb={8} bg="white" rounded={6} mx="auto" overflow="hidden">
              <Image
                source={{uri: item.sponsorLogo}} resizeMode="contain"
                style={[MainStyle.absolute, MainStyle.h_234, MainStyle.t__81, MainStyle.w_184, MainStyle.l__56]}
              />
            </Div>
          ) : null}
        </>
      )}

      {isFullSwing ? (
        <Div row justifyContent="space-between" mx={22} mb={27}>
          <Div>
            <P fontFamily="DavisSans-Medium" fs={10} tt="uppercase">TOURNEY ID</P>
            <P fontFamily="DavisSans-Medium" fs={14}>{item.id}</P>
          </Div>

          <Div>
            <P fontFamily="DavisSans-Medium" fs={10} tt="uppercase">Start</P>
            <P fontFamily="DavisSans-Medium" fs={13}>
              {moment.unix(Number(item.entryBeginDate)).format('ddd, MMM DD, yyyy')}
            </P>
            <P fontFamily="DavisSans-Medium" fs={13}>{moment.unix(Number(item.entryBeginDate)).format('hh:mm A')}</P>
          </Div>

          <Div>
            <P fontFamily="DavisSans-Medium" fs={10} tt="uppercase">End</P>
            <P fontFamily="DavisSans-Medium" fs={13}>
              {moment.unix(Number(item.entryEndDate)).format('ddd, MMM DD, yyyy')}
            </P>
            <P fontFamily="DavisSans-Medium" fs={13}>{moment.unix(Number(item.entryEndDate)).format('hh:mm A')}</P>
          </Div>
        </Div>
      ) : (
        <Div mx="auto" w={367}>
          <Div row mx={12} alignItems="center" mb={12}>
            <Div column flex={1}>
              <Div row mb={3} justifyContent="space-between">
                <P fw="500" fs={14} lh={17} tt="uppercase">Start</P>
                <P fw="600" lh={17}>{moment.unix(Number(item.entryBeginDate)).format('llll')}</P>
              </Div>

              <Div row justifyContent="space-between">
                <P fw="500" fs={14} lh={17} tt="uppercase">End</P>
                <P fw="600" lh={17}>{moment.unix(Number(item.entryEndDate)).format('llll')}</P>
              </Div>
            </Div>

            <P w={82} lh={36} ta="center" bw={1} ml={13} fw="700" fs={14} tt="uppercase" borderColor={statusStyle.color}>
              {statusStyle.text}
            </P>
          </Div>
        </Div>
      )}

      {!isFullSwing && <TournamentHeader mb={16} item={item} onReload={props.getUser}/>}

      <PrizePool
        mx={10} maxH={206} state={item?.state} prizePoolList={item.prizePool} entryfee={item.entryFee}
        playerDetailsList={item.playerDetails} guaranteedPricePool={item.minimumGuaranteedPricePool}
        prizeDistribution={prizeDistribution}
      />

      <Pressable style={MainStyle.m_x_auto} onPress={() => navigation.navigate('TournamentDetails', {id: item.id})}>
        <P lh={33} color={isFullSwing ? '#0AF' : colors.newGreenText} fw="700" fs={14} tt="uppercase">
          {isFullSwing ? 'Details' : 'View Details'}
        </P>
      </Pressable>
    </Div>
  );
};

export default React.memo(TournamentItem);
